import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatSecondsShort } from '../../utils/audio-segments';
import {
  MARKER_COLORS,
  MARKER_LABELS,
  MomentMarkerType,
  VideoMoment,
} from '../../utils/video-moments.utils';
import styles from './video-analytics.module.scss';

type Props = {
  videoUrl: string;
  durationSec: number;
  currentTime: number;
  markers: VideoMoment[];
  onTimeChange: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  onSeek: (time: number) => void;
  seekRef?: React.MutableRefObject<((time: number) => void) | null>;
  togglePlayPauseRef?: React.MutableRefObject<(() => void) | null>;
  wide?: boolean;
  embedded?: boolean;
};

const SmartVideoPlayer: React.FC<Props> = ({
  videoUrl,
  durationSec,
  currentTime,
  markers,
  onTimeChange,
  onDurationChange,
  onPlayingChange,
  onSeek,
  seekRef,
  togglePlayPauseRef,
  wide = false,
  embedded = false,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoDuration, setVideoDuration] = useState(durationSec);

  const effectiveDuration = videoDuration > 0 ? videoDuration : durationSec;

  const seekTo = useCallback((time: number) => {
    const el = videoRef.current;
    const clamped = Math.max(0, Math.min(time, effectiveDuration || time));
    if (el) {
      el.currentTime = clamped;
      void el.play().catch(() => {});
    }
    onTimeChange(clamped);
  }, [effectiveDuration, onTimeChange]);

  useEffect(() => {
    if (!seekRef) return;
    seekRef.current = (time: number) => {
      const el = videoRef.current;
      const dur = videoDuration > 0 ? videoDuration : durationSec;
      const clamped = Math.max(0, Math.min(time, dur || time));
      if (el) {
        el.currentTime = clamped;
        void el.play().catch(() => {});
      }
      onTimeChange(clamped);
    };
    return () => {
      seekRef.current = null;
    };
  }, [seekRef, videoDuration, durationSec, onTimeChange]);

  useEffect(() => {
    if (!togglePlayPauseRef) return;
    togglePlayPauseRef.current = () => {
      const el = videoRef.current;
      if (!el) return;
      if (el.paused) {
        void el.play().catch(() => {});
      } else {
        el.pause();
      }
    };
    return () => {
      togglePlayPauseRef.current = null;
    };
  }, [togglePlayPauseRef]);

  const progressPct = effectiveDuration > 0
    ? Math.min(100, (currentTime / effectiveDuration) * 100)
    : 0;

  const activeMarkerKey = useMemo(() => {
    let best: VideoMoment | undefined;
    let bestDelta = Infinity;
    markers.forEach(m => {
      const delta = Math.abs(m.seconds - currentTime);
      if (delta < 3 && delta < bestDelta) {
        best = m;
        bestDelta = delta;
      }
    });
    return best ? `${best.seconds}-${best.type}` : null;
  }, [markers, currentTime]);

  const handleBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!effectiveDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(ratio * effectiveDuration);
  };

  const legendTypes: MomentMarkerType[] = ['dispute', 'KPI', 'decision', 'risk', 'task'];

  return (
    <div className={`${styles.playerRoot} ${wide ? styles.playerWide : ''} ${embedded ? styles.playerEmbedded : ''}`}>
      <div className={styles.playerVideoWrap}>
        <video
          ref={videoRef}
          className={styles.playerVideo}
          src={videoUrl}
          controls
          preload="metadata"
          onTimeUpdate={() => {
            const t = videoRef.current?.currentTime ?? 0;
            onTimeChange(t);
          }}
          onPlay={() => onPlayingChange?.(true)}
          onPause={() => onPlayingChange?.(false)}
          onEnded={() => onPlayingChange?.(false)}
          onLoadedMetadata={() => {
            const d = videoRef.current?.duration ?? 0;
            if (d > 0) {
              setVideoDuration(d);
              onDurationChange(d);
            }
          }}
          onSeeked={() => {
            const t = videoRef.current?.currentTime ?? 0;
            onTimeChange(t);
          }}
        />
      </div>

      <div className={styles.smartTimeline}>
        <div className={styles.smartTimelineMeta}>
          <span className={styles.smartTimelineTitle}>Видеоплеер со смарт-маркерами</span>
          <span className={styles.smartTimelineTime}>
            {formatSecondsShort(currentTime)} / {formatSecondsShort(effectiveDuration)}
          </span>
        </div>

        <div
          className={styles.smartTimelineBar}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={effectiveDuration}
          aria-valuenow={currentTime}
          tabIndex={0}
          onClick={handleBarClick}
          onKeyDown={e => {
            if (e.key === 'ArrowRight') seekTo(currentTime + 5);
            if (e.key === 'ArrowLeft') seekTo(Math.max(0, currentTime - 5));
          }}
        >
          <div className={styles.smartTimelineFill} style={{ width: `${progressPct}%` }} />
          <div className={styles.smartTimelinePlayhead} style={{ left: `${progressPct}%` }} />
          {markers.map(m => {
            const left = effectiveDuration > 0 ? (m.seconds / effectiveDuration) * 100 : 0;
            const isActive = activeMarkerKey === `${m.seconds}-${m.type}`;
            return (
              <button
                key={`${m.seconds}-${m.type}-${m.label.slice(0, 12)}`}
                type="button"
                className={`${styles.smartTimelineMarker} ${isActive ? styles.smartTimelineMarkerActive : ''}`}
                style={{
                  left: `${Math.min(99.5, Math.max(0, left))}%`,
                  backgroundColor: MARKER_COLORS[m.type],
                }}
                title={`${MARKER_LABELS[m.type]} · ${formatSecondsShort(m.seconds)} · ${m.label}`}
                onClick={e => {
                  e.stopPropagation();
                  seekTo(m.seconds);
                }}
              />
            );
          })}
        </div>

        <div className={styles.smartTimelineLegend}>
          {legendTypes.map(type => (
            <span key={type} className={styles.smartTimelineLegendItem}>
              <span
                className={styles.smartTimelineLegendDot}
                style={{ backgroundColor: MARKER_COLORS[type] }}
              />
              {MARKER_LABELS[type]}
            </span>
          ))}
          {markers.length > 0 && (
            <span className={styles.smartTimelineLegendCount}>{markers.length} точек</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartVideoPlayer;
